import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ServerJob, ServerJobStatus } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  PowerOff,
  RefreshCw,
  Timer,
  Trash2,
  X,
} from 'lucide-react';

function formatRelative(iso: string): string {
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const abs = Math.abs(diff);
    const suffix = diff >= 0 ? '전' : '후';
    const sec = Math.floor(abs / 1000);
    if (sec < 60) return `${sec}초 ${suffix}`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}분 ${suffix}`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}시간 ${suffix}`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}일 ${suffix}`;
    return new Date(iso).toLocaleString('ko-KR');
  } catch {
    return iso;
  }
}

const STATUS_META: Record<
  ServerJobStatus,
  { label: string; tone: 'running' | 'queued' | 'warn' | 'error'; icon: typeof Loader2 }
> = {
  running: { label: '실행 중', tone: 'running', icon: Loader2 },
  queued: { label: '대기', tone: 'queued', icon: Timer },
  scheduled: { label: '예약', tone: 'queued', icon: Timer },
  failing: { label: '재시도 대기', tone: 'warn', icon: AlertCircle },
  permanently_failed: { label: '영구 실패', tone: 'error', icon: AlertCircle },
  interrupted_by_restart: { label: '서버 재시작으로 중단', tone: 'warn', icon: PowerOff },
};

const TASK_LABEL: Record<string, string> = {
  'data-source.sync-structure': '구조 동기화',
  'data-source.sync-stats': '통계 동기화',
  'data-source.generate-descriptions': 'LLM 설명 생성',
  'excel.ingest': 'Excel 업로드',
  retention_cleanup: '보존 정책 정리',
};

function StatusPill({ status }: { status: ServerJobStatus }) {
  const m = STATUS_META[status];
  const Icon = m.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md h-6 px-2 text-xs',
        m.tone === 'running' && 'bg-accent/10 text-accent',
        m.tone === 'queued' && 'bg-surface-muted text-foreground-muted',
        m.tone === 'warn' && 'bg-[hsl(45_85%_50%)]/10 text-[hsl(40_85%_40%)]',
        m.tone === 'error' && 'bg-destructive/10 text-destructive',
      )}
    >
      <Icon className={cn('size-3.5', status === 'running' && 'animate-spin')} />
      {m.label}
    </span>
  );
}

function describePayload(
  taskIdentifier: string,
  payload: Record<string, unknown> | null,
): string | null {
  if (!payload) return null;
  if (taskIdentifier.startsWith('data-source.')) {
    const ids = payload.tableIds;
    if (Array.isArray(ids) && ids.length > 0) return `테이블 ${ids.length}개 지정`;
    if (payload.fullReset === true) return '전체 다시 (fullReset)';
    return '전체 데이터소스';
  }
  return null;
}

function formatProgress(
  done: number | null,
  total: number | null,
  item: string | null,
): string | null {
  if (total === null || total === 0) return null;
  const d = done ?? 0;
  const pct = Math.round((d / total) * 100);
  if (item) return `${d}/${total} (${pct}%) · 현재: ${item}`;
  return `${d}/${total} (${pct}%)`;
}

export function AdminServerJobs() {
  const qc = useQueryClient();
  const jobsQuery = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: () => api.adminJobsList(),
    refetchInterval: 3000,
  });

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => api.adminJobCancel(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '작업 취소에 실패했습니다.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (jobId: string) => api.adminJobDelete(jobId),
    onSuccess: (res) => {
      if (!res.deleted && res.reason) {
        alert(`삭제 실패: ${res.reason}`);
      }
      qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '작업 삭제에 실패했습니다.');
    },
  });

  const jobs = jobsQuery.data?.jobs ?? [];
  const nameMap = jobsQuery.data?.dataSourceNames ?? {};

  return (
    <PageShell
      header={
        <PageHeader
          title="서버 작업"
          count={jobs.length}
          description="graphile-worker 큐의 실행 중·대기·실패 작업. 3초마다 자동 갱신."
          actions={
            <Button
              variant="secondary"
              onClick={() => qc.invalidateQueries({ queryKey: ['admin', 'jobs'] })}
              disabled={jobsQuery.isFetching}
            >
              <RefreshCw className={cn('size-4', jobsQuery.isFetching && 'animate-spin')} />
              새로고침
            </Button>
          }
        />
      }
    >
      {jobsQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : jobsQuery.error ? (
        <p className="text-sm text-destructive">
          {jobsQuery.error instanceof ApiError
            ? jobsQuery.error.message
            : '작업 목록을 불러오지 못했습니다.'}
        </p>
      ) : jobs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-6 py-16 text-center">
          <CheckCircle2 className="size-6 mx-auto mb-2 text-[hsl(150_60%_45%)]" />
          <p className="text-sm text-foreground-muted">
            현재 대기 중이거나 실행 중인 작업이 없습니다.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
              <tr>
                <Th>상태</Th>
                <Th>작업 종류</Th>
                <Th>대상</Th>
                <Th>실행 시각 / 예약</Th>
                <Th>시도</Th>
                <Th>최근 오류</Th>
                <Th w="120px">조치</Th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <JobRow
                  key={j.id}
                  job={j}
                  dataSourceName={j.dataSourceId ? (nameMap[j.dataSourceId] ?? null) : null}
                  onCancel={() => {
                    if (
                      window.confirm(
                        `${TASK_LABEL[j.taskIdentifier] ?? j.taskIdentifier} 작업을 취소하시겠습니까?\n실행 중이라도 영구 실패로 표시됩니다.`,
                      )
                    ) {
                      cancelMutation.mutate(j.id);
                    }
                  }}
                  onDelete={() => {
                    if (
                      window.confirm(
                        '이 작업을 큐에서 영구 삭제합니다.\n진행 중인 작업이라면 먼저 취소하세요. 계속?',
                      )
                    ) {
                      deleteMutation.mutate(j.id);
                    }
                  }}
                  canceling={cancelMutation.isPending && cancelMutation.variables === j.id}
                  deleting={deleteMutation.isPending && deleteMutation.variables === j.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

function Th({ children, w }: { children: React.ReactNode; w?: string }) {
  return (
    <th
      scope="col"
      className="px-3 py-2.5 font-medium text-left"
      style={w ? { width: w } : undefined}
    >
      {children}
    </th>
  );
}

function JobRow({
  job,
  dataSourceName,
  onCancel,
  onDelete,
  canceling,
  deleting,
}: {
  job: ServerJob;
  dataSourceName: string | null;
  onCancel: () => void;
  onDelete: () => void;
  canceling: boolean;
  deleting: boolean;
}) {
  const taskLabel = TASK_LABEL[job.taskIdentifier] ?? job.taskIdentifier;
  const target =
    dataSourceName ?? (job.dataSourceId ? `삭제된 소스(${job.dataSourceId.slice(0, 8)}…)` : '—');
  const payloadHint = describePayload(job.taskIdentifier, job.payload);
  const progressLabel = formatProgress(job.progressDone, job.progressTotal, job.progressItem);
  const cancelDisabled =
    canceling || job.status === 'permanently_failed' || job.status === 'interrupted_by_restart';
  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-3">
        <StatusPill status={job.status} />
      </td>
      <td className="px-3 py-3">
        <div className="font-medium">{taskLabel}</div>
        <div className="text-[11px] text-foreground-muted font-mono mt-0.5">
          {job.taskIdentifier}
        </div>
        {payloadHint ? (
          <div className="text-[11px] text-foreground-muted mt-0.5">{payloadHint}</div>
        ) : null}
      </td>
      <td className="px-3 py-3">
        <div>{target}</div>
        {progressLabel ? (
          <div className="text-[11px] text-foreground-muted mt-0.5">{progressLabel}</div>
        ) : null}
        {job.dataSourceId ? (
          <div
            className="text-[10px] text-foreground-muted/60 font-mono mt-0.5"
            title={job.dataSourceId}
          >
            {job.dataSourceId.slice(0, 8)}…
          </div>
        ) : null}
      </td>
      <td className="px-3 py-3 text-xs text-foreground-muted">
        {job.runStartedAt ? <div>실행 시작: {formatRelative(job.runStartedAt)}</div> : null}
        {job.lockedAt ? (
          <>
            <div>잠김: {formatRelative(job.lockedAt)}</div>
            <div className="opacity-70 truncate max-w-[160px]" title={job.lockedBy ?? ''}>
              worker: {job.lockedBy ?? '?'}
            </div>
          </>
        ) : (
          <div>
            {job.status === 'scheduled' ? '예약: ' : '대기 이후 실행: '}
            {formatRelative(job.runAt)}
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-xs font-mono">
        {job.attempts} / {job.maxAttempts}
      </td>
      <td className="px-3 py-3 text-xs text-foreground-muted max-w-[320px]">
        {job.lastError ? (
          <span className="block truncate" title={job.lastError}>
            {job.lastError}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelDisabled}
            aria-label="작업 취소"
            className="inline-flex items-center justify-center rounded-md size-7 text-foreground-muted hover:text-foreground hover:bg-surface-muted disabled:opacity-50"
            title={
              job.status === 'permanently_failed'
                ? '이미 영구 실패한 작업입니다. 휴지통으로 영구 삭제 가능.'
                : job.status === 'interrupted_by_restart'
                  ? '서버 재시작으로 이미 중단된 작업입니다. 휴지통으로 영구 삭제 가능.'
                  : '이 작업을 취소하고 진행 중이라면 가능한 빨리 멈춥니다.'
            }
          >
            {canceling ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting || job.lockedAt !== null}
            aria-label="작업 영구 삭제"
            className="inline-flex items-center justify-center rounded-md size-7 text-destructive/70 hover:text-destructive hover:bg-destructive/10 disabled:opacity-30"
            title={
              job.lockedAt
                ? '실행 중인 작업은 먼저 취소(X)하세요.'
                : '이 작업을 큐에서 영구 삭제합니다.'
            }
          >
            {deleting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Trash2 className="size-3.5" />
            )}
          </button>
        </div>
      </td>
    </tr>
  );
}
